import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type DetailTab = 'activity' | 'schedule' | 'configuration';
const ALL_TABS: ReadonlyArray<DetailTab> = ['activity', 'schedule', 'configuration'];

export interface ConnectorDetailPageState {
  tab: DetailTab;
  executionId: string | null;
  setTab: (tab: DetailTab) => void;
  /**
   * Select an execution. A user's pick pushes a history entry; pass
   * `{ replace: true }` for an automatic default so it adds none (#2161).
   */
  setExecutionId: (id: string | null, opts?: { replace?: boolean }) => void;
}

export function useConnectorDetailPageState(): ConnectorDetailPageState {
  const [params, setParams] = useSearchParams();

  const tab = useMemo<DetailTab>(() => {
    const raw = params.get('tab');
    return ALL_TABS.includes(raw as DetailTab) ? (raw as DetailTab) : 'activity';
  }, [params]);

  const executionId = params.get('executionId');

  const setTab = useCallback((next: DetailTab) => {
    setParams((prev) => {
      const updated = new URLSearchParams(prev);
      if (next === 'activity') updated.delete('tab');
      else updated.set('tab', next);
      return updated;
    }, { replace: false });
  }, [setParams]);

  const setExecutionId = useCallback((id: string | null, opts?: { replace?: boolean }) => {
    setParams((prev) => {
      const updated = new URLSearchParams(prev);
      if (id === null) updated.delete('executionId');
      else updated.set('executionId', id);
      return updated;
    }, { replace: opts?.replace ?? false });
  }, [setParams]);

  return { tab, executionId, setTab, setExecutionId };
}
